import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { FileText, CheckCircle, Clock, ArrowLeft, Download, Eye, Award } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/api/api';
import { formatDate } from '../../utils/dateTimeUtils';

const SubmissionList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assignmentId = searchParams.get('assignment_id');
  
  const [submissions, setSubmissions] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(assignmentId || '');
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gradeModalOpen, setGradeModalOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [gradeData, setGradeData] = useState({ marks: 0, feedback: '' });

  useEffect(() => {
    fetchAssignments();
  }, []);

  useEffect(() => {
    if (selectedAssignment) {
      fetchSubmissions();
      fetchAssignmentDetails();
    }
  }, [selectedAssignment]);

  const fetchAssignments = async () => {
    try {
      const response = await api.get('/lms/assignments');
      setAssignments(response.data || []);
      
      // Auto-select if assignment_id in URL
      if (assignmentId && !selectedAssignment) {
        setSelectedAssignment(assignmentId);
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  const fetchAssignmentDetails = async () => {
    try {
      const response = await api.get(`/lms/assignments/${selectedAssignment}`);
      setAssignment(response.data);
    } catch (error) {
      console.error('Error fetching assignment details:', error);
    }
  };

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/lms/submissions', { params: { assignment_id: selectedAssignment } });
      setSubmissions(response.data || []);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const openGradeModal = (submission) => {
    setSelectedSubmission(submission);
    setGradeData({
      marks: submission.marks_obtained || 0,
      feedback: submission.feedback || ''
    });
    setGradeModalOpen(true);
  };

  const handleGrade = async () => {
    if (!selectedSubmission) return;
    
    try {
      await api.post(`/lms/submissions/${selectedSubmission.id}/grade`, {
        marks: gradeData.marks,
        feedback: gradeData.feedback
      });
      toast.success('Submission graded successfully');
      setGradeModalOpen(false);
      fetchSubmissions();
    } catch (error) {
      console.error('Error grading submission:', error);
      toast.error(error.response?.data?.detail || 'Failed to grade submission');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'graded':
        return <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded flex items-center gap-1"><CheckCircle size={12} /> Graded</span>;
      case 'submitted':
        return <span className="bg-yellow-100 text-yellow-700 text-xs px-2 py-1 rounded flex items-center gap-1"><Clock size={12} /> Pending Review</span>;
      default:
        return <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">{status}</span>;
    }
  };

  return (
    <div className="space-y-6" data-testid="lms-submission-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/lms/assignments')} size="sm">
            <ArrowLeft size={18} className="mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Review Submissions</h1>
            <p className="text-gray-600">Grade and provide feedback on student submissions</p>
          </div>
        </div>
      </div>

      {/* Assignment Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Select Assignment</label>
              <Select value={selectedAssignment} onValueChange={setSelectedAssignment}>
                <SelectTrigger data-testid="assignment-select">
                  <SelectValue placeholder="Choose an assignment to review" />
                </SelectTrigger>
                <SelectContent>
                  {assignments.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.title} - Due: {formatDate(a.due_date)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {assignment && (
              <div className="bg-blue-50 p-3 rounded-lg text-sm">
                <p><strong>Total Marks:</strong> {assignment.total_marks}</p>
                <p><strong>Submissions:</strong> {submissions.length}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Submissions List */}
      {!selectedAssignment ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-500">Select an assignment to view submissions</p>
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : submissions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-500">No submissions yet for this assignment</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {submissions.map((submission) => (
            <Card key={submission.id} className="hover:shadow-md transition-shadow" data-testid={`submission-${submission.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-lg ${submission.status === 'graded' ? 'bg-green-100' : 'bg-yellow-100'}`}>
                      <FileText className={submission.status === 'graded' ? 'text-green-600' : 'text-yellow-600'} size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{submission.student_name}</h3>
                      <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                        <span>Submitted: {new Date(submission.submitted_at).toLocaleString('en-IN')}</span>
                        {submission.is_late && (
                          <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded">Late</span>
                        )}
                        {submission.resubmission_count > 0 && (
                          <span className="text-xs text-gray-400">Resubmitted {submission.resubmission_count}x</span>
                        )}
                      </div>
                      {submission.status === 'graded' && (
                        <div className="mt-2 flex items-center gap-2">
                          <Award className="text-green-600" size={16} />
                          <span className="font-medium text-green-700">
                            {submission.marks_obtained} / {assignment?.total_marks || 100} marks
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(submission.status)}
                    
                    {submission.content && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => window.open(submission.content, '_blank')}
                      >
                        <Eye size={16} className="mr-1" /> View
                      </Button>
                    )}
                    
                    <Button 
                      onClick={() => openGradeModal(submission)}
                      className={submission.status === 'graded' ? 'bg-gray-600 hover:bg-gray-700' : 'bg-green-600 hover:bg-green-700'}
                      size="sm"
                      data-testid={`grade-btn-${submission.id}`}
                    >
                      {submission.status === 'graded' ? 'Update Grade' : 'Grade'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Grade Modal */}
      <Dialog open={gradeModalOpen} onOpenChange={setGradeModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Grade Submission</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-sm"><strong>Student:</strong> {selectedSubmission?.student_name}</p>
              <p className="text-sm"><strong>Submitted:</strong> {selectedSubmission && new Date(selectedSubmission.submitted_at).toLocaleString('en-IN')}</p>
            </div>
            
            <div>
              <label className="text-sm font-medium">Marks (out of {assignment?.total_marks || 100}) *</label>
              <Input
                type="number"
                value={gradeData.marks}
                onChange={(e) => setGradeData({...gradeData, marks: parseInt(e.target.value) || 0})}
                max={assignment?.total_marks || 100}
                min={0}
                data-testid="marks-input"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Feedback</label>
              <Textarea
                value={gradeData.feedback}
                onChange={(e) => setGradeData({...gradeData, feedback: e.target.value})}
                placeholder="Provide feedback for the student..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGradeModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleGrade} className="bg-green-600 hover:bg-green-700" data-testid="submit-grade-btn">
              Save Grade
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubmissionList;
